import { Button } from '@1trading/react/components/ui/button';
import { FormStepper, useFormStepper } from '@1trading/react/components/ui/form-stepper';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import type { GoToSummaryButtonProps } from '@/components/ui/go-to-summary-button';
import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeadingBackButton } from '@/components/ui/page-heading';
import { useProgress } from '@/context/progress';
import { type RouteConfig, useRouteConfig } from '@/context/route-config';
import {
  AML_QUESTIONNAIRE_DEFAULTS,
  type AmlQuestionnaire,
  amlQuestionnaireCryptoDepositSchema,
  amlQuestionnaireEmploymentDetailsSchema,
  amlQuestionnaireEmploymentStatusSchema,
  amlQuestionnaireFiatDepositSchema,
  amlQuestionnaireInvestmentSchema,
  amlQuestionnairePepSchema,
  amlQuestionnairePurposeAndFundsSchema,
  amlQuestionnaireTradingVolumeSchema,
  amlQuestionnaireWithdrawalSchema,
  amlQuestionnaireWithIdentitySchema,
} from '@/features/onboarding/aml-questionnaire/schema';
import { useIdentity } from '@/features/onboarding/personal-information/hooks/use-identity';
import PeriodicReviewGoToSummaryButton from '@/features/periodic-review/components/go-to-summary-button';
import { useIsPeriodicReview } from '@/features/periodic-review/context';
import { useProfileAmlMutation } from '@/hooks/queries/use-profile-aml';
import { useProfilePeriodicReviewAml, useProfilePeriodicReviewAmlMutation } from '@/hooks/queries/use-profile-periodic-review';
import { useProfileVerificationInformation } from '@/hooks/queries/use-profile-verification-information';
import { AmlRiskLevel } from '@/services/api/profile';
import type { FormStep } from '@/types/components';
import { AmlQuestionnaireFieldsetCryptoDeposit, AmlQuestionnaireFieldsetCryptoDepositLegend } from './fieldsets/crypto-deposit';
import { AmlQuestionnaireFieldsetEmploymentDetails, AmlQuestionnaireFieldsetEmploymentDetailsLegend } from './fieldsets/employment-details';
import { AmlQuestionnaireFieldsetEmploymentStatus, AmlQuestionnaireFieldsetEmploymentStatusLegend } from './fieldsets/employment-status';
import { AmlQuestionnaireFieldsetFiatDeposit, AmlQuestionnaireFieldsetFiatDepositLegend } from './fieldsets/fiat-deposit';
import { AmlQuestionnaireFieldsetInvestment, AmlQuestionnaireFieldsetInvestmentLegend } from './fieldsets/investment';
import { AmlQuestionnaireFieldsetPep, AmlQuestionnaireFieldsetPepLegend } from './fieldsets/pep';
import { AmlQuestionnaireFieldsetPurposeAndFunds, AmlQuestionnaireFieldsetPurposeAndFundsLegend } from './fieldsets/purpose-and-funds';
import { AmlQuestionnaireFieldsetTradingVolume, AmlQuestionnaireFieldsetTradingVolumeLegend } from './fieldsets/trading-volume';
import { AmlQuestionnaireFieldsetWithdrawal, AmlQuestionnaireFieldsetWithdrawalLegend } from './fieldsets/withdrawal';

export interface AmlQuestionnaireRouteConfig extends RouteConfig {
  next: string;
  personalInformation: string;
}

const FORM_STEPS = {
  PEP: {
    Component: AmlQuestionnaireFieldsetPep,
    Legend: AmlQuestionnaireFieldsetPepLegend,
    schema: amlQuestionnairePepSchema,
  },
  EMPLOYMENT_STATUS: {
    Component: AmlQuestionnaireFieldsetEmploymentStatus,
    Legend: AmlQuestionnaireFieldsetEmploymentStatusLegend,
    schema: amlQuestionnaireEmploymentStatusSchema,
  },
  EMPLOYMENT_DETAILS: {
    Component: AmlQuestionnaireFieldsetEmploymentDetails,
    Legend: AmlQuestionnaireFieldsetEmploymentDetailsLegend,
    schema: amlQuestionnaireEmploymentDetailsSchema,
  },
  INVESTMENT: {
    Component: AmlQuestionnaireFieldsetInvestment,
    Legend: AmlQuestionnaireFieldsetInvestmentLegend,
    schema: amlQuestionnaireInvestmentSchema,
  },
  WITHDRAWAL: {
    Component: AmlQuestionnaireFieldsetWithdrawal,
    Legend: AmlQuestionnaireFieldsetWithdrawalLegend,
    schema: amlQuestionnaireWithdrawalSchema,
  },
  FIAT_DEPOSIT: {
    Component: AmlQuestionnaireFieldsetFiatDeposit,
    Legend: AmlQuestionnaireFieldsetFiatDepositLegend,
    schema: amlQuestionnaireFiatDepositSchema,
  },
  CRYPTO_DEPOSIT: {
    Component: AmlQuestionnaireFieldsetCryptoDeposit,
    Legend: AmlQuestionnaireFieldsetCryptoDepositLegend,
    schema: amlQuestionnaireCryptoDepositSchema,
  },
  TRADING_VOLUME: {
    Component: AmlQuestionnaireFieldsetTradingVolume,
    Legend: AmlQuestionnaireFieldsetTradingVolumeLegend,
    schema: amlQuestionnaireTradingVolumeSchema,
  },
  PURPOSE_AND_FUNDS: {
    Component: AmlQuestionnaireFieldsetPurposeAndFunds,
    Legend: AmlQuestionnaireFieldsetPurposeAndFundsLegend,
    schema: amlQuestionnairePurposeAndFundsSchema,
  },
} satisfies Record<string, FormStep<AmlQuestionnaire>>;

const STEPS = Object.values(FORM_STEPS).map((step) => step.Component);
const STEP_LEGENDS = Object.values(FORM_STEPS).map((step) => step.Legend);
const STEP_SCHEMAS = Object.values(FORM_STEPS).map((step) => step.schema);

const AmlQuestionnaireFormCurrentStep = () => {
  const { setProgress } = useProgress();
  const { currentStep, totalSteps } = useFormStepper();
  const Step = STEPS[currentStep];

  useEffect(() => {
    return setProgress(currentStep / totalSteps);
  }, [currentStep, setProgress, totalSteps]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to scroll when currentStep changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  if (!Step) throw new Error('Step not found');

  return (
    <PageCardContent className="pt-8">
      <div className="animate-in fade-in" key={currentStep}>
        <Step />
      </div>
    </PageCardContent>
  );
};
AmlQuestionnaireFormCurrentStep.displayName = 'AmlQuestionnaireFormCurrentStep';

const AmlQuestionnaireFormCurrentStepLegend = () => {
  const { currentStep } = useFormStepper();
  const Legend = STEP_LEGENDS[currentStep];

  if (!Legend) throw new Error('Legend not found');

  return <Legend />;
};
AmlQuestionnaireFormCurrentStepLegend.displayName = 'AmlQuestionnaireFormCurrentStepLegend';

const AmlQuestionnaireFormBackButton = () => {
  const { currentStep, prevStep } = useFormStepper();

  const handlePrevStep = useCallback(() => prevStep(), [prevStep]);

  if (currentStep === 0) return null;

  return <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />;
};
AmlQuestionnaireFormBackButton.displayName = 'AmlQuestionnaireFormBackButton';

const AmlQuestionnaireFormFooter = ({ isPending }: { isPending: boolean }) => {
  const { currentStep, isLastStep, nextStep } = useFormStepper();
  const isPeriodicReview = useIsPeriodicReview();
  const { t } = useTranslation();
  const Step = STEPS[currentStep];

  if (!isPeriodicReview && Step && 'isAutoNextStep' in Step && Step.isAutoNextStep) return null;

  return (
    <PageCardFooter>
      <Button className="w-full relative" size="lg" variant="primary" disabled={isPending} onClick={() => nextStep()}>
        <span className={isPending ? 'invisible' : ''}>{t(isLastStep && !isPeriodicReview ? 'buttons.submit' : 'buttons.continue')}</span>
        {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
      </Button>
    </PageCardFooter>
  );
};
AmlQuestionnaireFormFooter.displayName = 'AmlQuestionnaireFormFooter';

const AmlQuestionnaireFormGoToSummaryButton = (props: Omit<GoToSummaryButtonProps<AmlQuestionnaire>, 'onIncompleteSection'>) => {
  const { nextStep } = useFormStepper();

  return <PeriodicReviewGoToSummaryButton onIncompleteSection={() => nextStep()} {...props} />;
};
AmlQuestionnaireFormGoToSummaryButton.displayName = 'AmlQuestionnaireFormGoToSummaryButton';

const AmlQuestionnaireForm = () => {
  const isPeriodicReview = useIsPeriodicReview();
  const { isPending: isPendingLive, mutate } = useProfileAmlMutation();
  const { isPending: isPendingDraft, mutate: mutateDraft } = useProfilePeriodicReviewAmlMutation();
  const { isLoading: isLoadingDraft, data: draft } = useProfilePeriodicReviewAml(isPeriodicReview);
  const { isComplete: isCompleteIdentity, isLoading: isLoadingIdentity, data: identity } = useIdentity();
  const { isLoading: isLoadingVerificationInformation, data: verificationInformation, set: setVerificationInformation } = useProfileVerificationInformation();
  const routeConfig = useRouteConfig<AmlQuestionnaireRouteConfig>();
  const isLoading = isLoadingIdentity || isLoadingDraft || (!isPeriodicReview && isLoadingVerificationInformation);
  const isPending = isPendingLive || isPendingDraft;
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  const onError = () => {
    toast({
      description: t('fundamentals.defaultError.message'),
      variant: 'destructive',
    });
  };

  const onSubmit = async (data: AmlQuestionnaire, { isReturningToSummary = false }: { isReturningToSummary?: boolean } = {}) => {
    try {
      const amlAnswers = await amlQuestionnaireWithIdentitySchema.parseAsync({
        ...data,
        birthCountry: identity?.birthInfo?.country,
        birthDate: identity?.birthInfo?.date,
        residencyCountry: identity?.addressOfResidency?.country,
      });

      if (isPeriodicReview) {
        mutateDraft({ answers: amlAnswers }, { onError, onSuccess: () => navigate(routeConfig.next, { replace: true }) });

        return;
      }

      mutate(
        { answers: amlAnswers },
        {
          onError,
          onSuccess: (data) => {
            setVerificationInformation({ aml: { date: new Date().toISOString() } });
            navigate(data.riskLevel === AmlRiskLevel.PROHIBITED ? '/onboarding/aml-questionnaire/ineligible' : routeConfig.next, { replace: true });
          },
        },
      );
    } catch (error) {
      // GoToSummaryButton catches this and moves the customer on to the incomplete step
      if (isReturningToSummary && error instanceof z.ZodError) throw error;

      onError();
    }
  };

  if (isLoading) return <LoadingCard />;

  if (!isCompleteIdentity) return <Navigate to={routeConfig.personalInformation} replace />;

  if (!isPeriodicReview) {
    if (!verificationInformation || verificationInformation.verification.status === 'NOT_VERIFIED') return <Navigate to="/onboarding/verification" replace />;

    if (verificationInformation.aml.date) return <Navigate to={routeConfig.next} replace />;
  }

  return (
    <FormStepper className="relative" defaultValues={{ ...AML_QUESTIONNAIRE_DEFAULTS, ...draft?.answers }} stepSchemas={STEP_SCHEMAS} onSubmit={onSubmit}>
      <fieldset>
        <AmlQuestionnaireFormCurrentStepLegend />
        <div className="flex flex-row justify-between -md:justify-end">
          <AmlQuestionnaireFormBackButton />
          <AmlQuestionnaireFormGoToSummaryButton disabled={isPending} onSubmit={(data: AmlQuestionnaire) => onSubmit(data, { isReturningToSummary: true })} />
        </div>
        <PageCard size="lg" bordered fixedFooter>
          <AmlQuestionnaireFormCurrentStep />
          <AmlQuestionnaireFormFooter isPending={isPending} />
        </PageCard>
      </fieldset>
    </FormStepper>
  );
};
AmlQuestionnaireForm.displayName = 'AmlQuestionnaireForm';

export default AmlQuestionnaireForm;
